import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SearchFilters = ({ filters, onFiltersChange, onReset, isLoading }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);

  const courtTypeOptions = [
    { value: 'all', label: 'Todos os tipos' },
    { value: 'grass', label: 'Grama Natural' },
    { value: 'synthetic', label: 'Grama Sintética' },
    { value: 'concrete', label: 'Concreto' },
    { value: 'indoor', label: 'Quadra Coberta' },
    { value: 'sand', label: 'Areia' }
  ];

  const timeSlotOptions = [
    { value: 'morning', label: 'Manhã (6h - 12h)' },
    { value: 'afternoon', label: 'Tarde (12h - 18h)' },
    { value: 'evening', label: 'Noite (18h - 22h)' },
    { value: 'late', label: 'Madrugada (22h - 6h)' }
  ];

  const sortOptions = [
    { value: 'distance', label: 'Distância' },
    { value: 'price_low', label: 'Menor Preço' },
    { value: 'price_high', label: 'Maior Preço' },
    { value: 'rating', label: 'Melhor Avaliação' },
    { value: 'availability', label: 'Disponibilidade' }
  ];

  const amenityOptions = [
    { value: 'parking', label: 'Estacionamento', icon: 'Car' },
    { value: 'changing_room', label: 'Vestiário', icon: 'Shirt' },
    { value: 'shower', label: 'Chuveiro', icon: 'Droplets' },
    { value: 'lighting', label: 'Iluminação', icon: 'Lightbulb' },
    { value: 'security', label: 'Segurança', icon: 'Shield' },
    { value: 'snack_bar', label: 'Lanchonete', icon: 'Coffee' },
    { value: 'equipment_rental', label: 'Aluguel de Equipamentos', icon: 'Package' },
    { value: 'wifi', label: 'Wi-Fi', icon: 'Wifi' }
  ];

  const handleFilterChange = (key, value) => {
    const updatedFilters = { ...localFilters, [key]: value };
    setLocalFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const handleAmenityChange = (amenityValue, checked) => {
    const currentAmenities = localFilters?.amenities || [];
    const updatedAmenities = checked
      ? [...currentAmenities, amenityValue]
      : currentAmenities?.filter(a => a !== amenityValue);
    
    handleFilterChange('amenities', updatedAmenities);
  };

  const handleReset = () => {
    const resetFilters = {
      location: '',
      radius: 5,
      priceMin: 0,
      priceMax: 200,
      courtType: 'all',
      timeSlot: '',
      date: '',
      amenities: [],
      sortBy: 'distance',
      availableOnly: false
    };
    setLocalFilters(resetFilters);
    onReset(resetFilters);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (localFilters?.location) count++;
    if (localFilters?.courtType !== 'all') count++;
    if (localFilters?.timeSlot) count++;
    if (localFilters?.date) count++;
    if (localFilters?.amenities?.length > 0) count++;
    if (localFilters?.availableOnly) count++;
    if (localFilters?.priceMin > 0 || localFilters?.priceMax < 200) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1">
      {/* Filter Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Filter" size={20} className="text-primary" />
          <h2 className="font-heading font-semibold text-card-foreground">
            Filtros de Busca
          </h2>
          {activeFiltersCount > 0 && (
            <div className="px-2 py-1 bg-primary text-primary-foreground rounded-full text-xs font-medium">
              {activeFiltersCount}
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {activeFiltersCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              iconName="RotateCcw"
              iconPosition="left"
              iconSize={14}
            >
              Limpar
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsExpanded(!isExpanded)}
            className="lg:hidden"
          >
            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
          </Button>
        </div>
      </div>
      {/* Filter Content */}
      <div className={`${isExpanded ? 'block' : 'hidden'} lg:block`}>
        <div className="p-4 space-y-6">
          {/* Location and Radius */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Localização"
              type="text"
              placeholder="Digite o endereço ou bairro"
              value={localFilters?.location}
              onChange={(e) => handleFilterChange('location', e?.target?.value)}
              className="w-full"
            />
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Raio de busca: {localFilters?.radius}km
              </label>
              <input
                type="range"
                min="1"
                max="50"
                value={localFilters?.radius}
                onChange={(e) => handleFilterChange('radius', parseInt(e?.target?.value))}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>1km</span>
                <span>50km</span>
              </div>
            </div>
          </div>

          {/* Price Range */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Faixa de Preço: R$ {localFilters?.priceMin} - R$ {localFilters?.priceMax}/hora
            </label>
            <div className="grid grid-cols-2 gap-4">
              <Input
                type="number"
                placeholder="Mín"
                value={localFilters?.priceMin}
                onChange={(e) => handleFilterChange('priceMin', parseInt(e?.target?.value) || 0)}
                min="0"
                max="500"
              />
              <Input
                type="number"
                placeholder="Máx"
                value={localFilters?.priceMax}
                onChange={(e) => handleFilterChange('priceMax', parseInt(e?.target?.value) || 200)}
                min="0"
                max="500"
              />
            </div>
          </div>

          {/* Court Type and Time Slot */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Tipo de Quadra"
              options={courtTypeOptions}
              value={localFilters?.courtType}
              onChange={(value) => handleFilterChange('courtType', value)}
            />
            
            <Select
              label="Horário Preferido"
              options={timeSlotOptions}
              value={localFilters?.timeSlot}
              onChange={(value) => handleFilterChange('timeSlot', value)}
              placeholder="Selecione um horário"
            />
          </div>

          {/* Date and Sort */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Data Desejada"
              type="date"
              value={localFilters?.date}
              onChange={(e) => handleFilterChange('date', e?.target?.value)}
              min={new Date()?.toISOString()?.split('T')?.[0]}
            />
            
            <Select
              label="Ordenar por"
              options={sortOptions}
              value={localFilters?.sortBy}
              onChange={(value) => handleFilterChange('sortBy', value)}
            />
          </div>

          {/* Availability Filter */}
          <div>
            <Checkbox
              label="Mostrar apenas quadras disponíveis"
              description="Filtrar quadras com horários livres na data selecionada"
              checked={localFilters?.availableOnly}
              onChange={(e) => handleFilterChange('availableOnly', e?.target?.checked)}
            />
          </div>

          {/* Amenities */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Comodidades
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {amenityOptions?.map((amenity) => (
                <div key={amenity?.value} className="flex items-center space-x-2">
                  <Checkbox
                    checked={localFilters?.amenities?.includes(amenity?.value) || false}
                    onChange={(e) => handleAmenityChange(amenity?.value, e?.target?.checked)}
                  />
                  <div className="flex items-center space-x-2">
                    <Icon name={amenity?.icon} size={14} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{amenity?.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Apply Filters Button - Mobile */}
        <div className="p-4 border-t border-border lg:hidden">
          <Button
            variant="default"
            fullWidth
            loading={isLoading}
            iconName="Search"
            iconPosition="left"
            iconSize={16}
          >
            Aplicar Filtros
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SearchFilters;